import { buildDispatchReassignmentMutationModeFields } from "./dispatchReassignmentMode.js";
import { markSourceChangesRefreshPerformed } from "./sourceChangeExactSelection.js";

function normalizeInlineText(value) {
  return typeof value === "string" ? value.trim() : "";
}

const SOURCE_CHANGES_FAST_REBUILD_REJECTED_CODE = "source_changes_fast_rebuild_rejected";
const SOURCE_CHANGES_PREVIEW_STALE_CODE = "source_changes_preview_stale";

function resolveOrderId(detail) {
  return normalizeInlineText(detail?.order_id || detail?.orderId);
}

function resolveRemoveModifiedCandidates(details = {}) {
  const safeDetails = details && typeof details === "object" && !Array.isArray(details)
    ? details
    : {};
  return Array.isArray(safeDetails.modified_same_date)
    ? safeDetails.modified_same_date.filter(Boolean)
    : [];
}

export function selectRemoveModifiedSourceChangeDetails(details = {}) {
  const protectedOrderIds = new Set(
    (Array.isArray(details?.protected_execution) ? details.protected_execution : [])
      .map(resolveOrderId)
      .filter(Boolean),
  );
  const seenOrderIds = new Set();
  return resolveRemoveModifiedCandidates(details).filter((detail) => {
    const orderId = resolveOrderId(detail);
    if (!orderId || protectedOrderIds.has(orderId) || seenOrderIds.has(orderId)) {
      return false;
    }
    seenOrderIds.add(orderId);
    return true;
  });
}

export function buildRemoveModifiedSourceChangeRequest(sourceChanges = {}, rebuildMode) {
  const details = sourceChanges?.details && typeof sourceChanges.details === "object"
    ? sourceChanges.details
    : {};
  const candidates = resolveRemoveModifiedCandidates(details);
  const selectedDetails = selectRemoveModifiedSourceChangeDetails(details);
  const orderIds = selectedDetails.map(resolveOrderId);
  const previewRevision = normalizeInlineText(
    sourceChanges?.mutationPreviewRevision || sourceChanges?.mutation_preview_revision,
  );

  return {
    hasIncompleteOrderIds: candidates.some((detail) => !resolveOrderId(detail)),
    orderIds,
    requestBody: {
      preview_revision: previewRevision,
      order_ids: orderIds,
      ...buildDispatchReassignmentMutationModeFields(rebuildMode),
    },
    selectedDetails,
  };
}

function haveSameOrderIds(leftOrderIds = [], rightOrderIds = []) {
  const normalizeOrderIds = (values) => [...new Set(
    (Array.isArray(values) ? values : [])
      .map(normalizeInlineText)
      .filter(Boolean),
  )].sort();
  const left = normalizeOrderIds(leftOrderIds);
  const right = normalizeOrderIds(rightOrderIds);
  return left.length === right.length && left.every((orderId, index) => orderId === right[index]);
}

export async function executeRemoveModifiedSourceChangeRequest({
  initialRequest,
  postRequest,
  refreshSourceChanges,
} = {}) {
  if (typeof postRequest !== "function" || typeof refreshSourceChanges !== "function") {
    throw new TypeError("Les fonctions de retrait et de rafraîchissement M-à-J sont requises.");
  }

  try {
    return await postRequest(initialRequest?.requestBody);
  } catch (error) {
    const errorCode = normalizeInlineText(error?.code);
    const isFastRebuildRejected = Number(error?.status) === 409
      && errorCode === SOURCE_CHANGES_FAST_REBUILD_REJECTED_CODE;
    const isStalePreview = Number(error?.status) === 409
      && errorCode === SOURCE_CHANGES_PREVIEW_STALE_CODE;
    if (isFastRebuildRejected || !isStalePreview) {
      throw error;
    }

    const clickedRebuildMode = initialRequest?.requestBody?.rebuild_mode;
    const stalePayload = error?.payload && typeof error.payload === "object"
      && !Array.isArray(error.payload)
      ? error.payload
      : null;
    const serverPreviewRevision = normalizeInlineText(stalePayload?.preview_revision);
    const serverOrderIds = Array.isArray(stalePayload?.order_ids)
      ? stalePayload.order_ids
      : null;
    const hasCompleteServerSelection = serverOrderIds !== null
      && serverOrderIds.every((orderId) => Boolean(normalizeInlineText(orderId)));
    if (serverPreviewRevision && hasCompleteServerSelection) {
      if (!haveSameOrderIds(initialRequest?.orderIds, serverOrderIds)) {
        throw error;
      }
      return postRequest({
        ...initialRequest.requestBody,
        preview_revision: serverPreviewRevision,
        order_ids: [...initialRequest.requestBody.order_ids],
        ...buildDispatchReassignmentMutationModeFields(clickedRebuildMode),
      });
    }

    let refreshedSourceChanges = null;
    try {
      refreshedSourceChanges = await refreshSourceChanges();
    } catch (refreshError) {
      markSourceChangesRefreshPerformed(refreshError);
      throw refreshError;
    }
    const refreshedRequest = buildRemoveModifiedSourceChangeRequest(
      refreshedSourceChanges,
      clickedRebuildMode,
    );
    const canRetry = Boolean(refreshedRequest.requestBody.preview_revision)
      && refreshedRequest.hasIncompleteOrderIds !== true
      && refreshedRequest.orderIds.length > 0
      && haveSameOrderIds(initialRequest?.orderIds, refreshedRequest.orderIds);
    if (!canRetry) {
      markSourceChangesRefreshPerformed(error);
      throw error;
    }

    try {
      return await postRequest({
        ...refreshedRequest.requestBody,
        order_ids: [...initialRequest.requestBody.order_ids],
        ...buildDispatchReassignmentMutationModeFields(clickedRebuildMode),
      });
    } catch (retryError) {
      markSourceChangesRefreshPerformed(retryError);
      throw retryError;
    }
  }
}
